import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { UpdateHistory as UpdateHistoryType } from '@/types';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

export const UpdateHistory: React.FC = () => {
  const [history, setHistory] = useState<UpdateHistoryType[]>([]);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await axios.get<UpdateHistoryType[]>('/updates/history');
        setHistory(response.data);
      } catch (error) {
        if (axios.isAxiosError(error)) {
          setError(error.response?.data?.message || error.message);
        } else {
          setError('Failed to fetch update history');
        }
      }
    };

    fetchHistory();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight">Update History</h2>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Version History</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            {history.length === 0 && !error && (
              <TableCaption>No update history available</TableCaption>
            )}
            <TableHeader>
              <TableRow>
                <TableHead>Version</TableHead>
                <TableHead>Release Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Min App Version</TableHead>
                <TableHead className="w-[300px]">Release Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((update) => (
                <TableRow key={update.version}>
                  <TableCell className="font-medium">{update.version}</TableCell>
                  <TableCell>{formatDate(update.releaseDate)}</TableCell>
                  <TableCell>
                    <Badge variant={ "secondary"}>
                      {update.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>{update.minAppVersion || 'N/A'}</TableCell>
                  <TableCell>
                    <div className="whitespace-pre-wrap">
                      {update.releaseNotes}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
